import * as PIXI from "pixi.js";
import { Symbol } from "../Symbol";
import { WinCounter } from "../WinCounter";
import { delay, moveObjectToNewParent } from "src/shared/util/UtilFunctions";
import {
  InitialResponse,
  RandomWild,
  SpinResponse,
  Win,
} from "src/shared/util/Types";
import { IReel, Reel, ReelState } from "src/panel/Reel";
import { ReelSpin } from "src/panel/ReelSpin";
import { GameEngine } from "../server/GameEngine";
import { GameAssets } from "src/shared/util/GameAssets";
import { Multiplier } from "../Multiplier";
import { Torch } from "../Torch";
import { BonusIntro } from "../bonus/BonusIntro";
import { GameState, GlobalState } from "src/shared/util/GlobalState";
import gsap from "gsap";
import { BonusWin } from "../bonus/BonusWin";
import { BonusCounter } from "../bonus/BonusCounter";
import { MultiplierPanel } from "../MultiplierPanel";
import { RetriggerPopup } from "../RetriggerPopup";
import { Spine } from "@pixi/spine-pixi";
import { ScatterParticle } from "src/ScatterParticle";
import { Payline } from "./Payline";
import { soundManager } from "src/shared/UI/SoundManager";
import { SoundInstance } from "src/shared/UI/SoundManagerUtils";
//
console.log("");

const STOP_REEL_INTERVAL: number = 400;
const STOP_REEL_ANTICIPATION_INTERVAL: number = 3000;
const SPIN_REEL_INTERVAL: number = 30;
// Define an enum for the states
export enum PanelState {
  IDLE = "IDLE",
  SPIN = "SPIN",
  SPIN_END = "SPIN_END",
  MATCH = "MATCH",
  CASCADE = "CASCADE",
  EXPANDED_WILD = "EXPANDED_WILD",
  RANDOM_WILD = "RANDOM_WILD",
  WIN = "WIN",
  STOPPING = "STOPPING",
  MULTIPLIER = "MULTIPLIER",
  SCATTER_MATCH = "SCATTER_MATCH",
  STOP = "STOP",
}

export class Panel extends PIXI.Container {
  private panelData!: string[][];
  private torches: Torch[] = [];
  private randomWilds: Symbol[] = [];
  private reels: IReel[] = [];
  private winSymbols: Symbol[] = [];
  private multipliers: Multiplier[] = [];
  private darkLayer: PIXI.Graphics;
  private winLayer: PIXI.Graphics;
  private panelMask: PIXI.Graphics;
  private panelBack: PIXI.Sprite;
  private panelFront: PIXI.Sprite;
  private curtains: Spine;
  private topContainer: PIXI.Container;
  private paylineContainer: PIXI.Container;
  private bottomContainer: PIXI.Container;
  private currentState: PanelState = PanelState.IDLE;
  private winCounter!: WinCounter;
  private content: PIXI.Container;
  private currentReelIndex: number = 0;
  private currentSccatersAmount: number = 0;
  private isQuickStop: boolean = false;
  private data!: SpinResponse;
  private stopNextId!: NodeJS.Timeout | null;
  private roundWin: number = 0;
  private bonusIntro!: BonusIntro;
  private bonusWin!: BonusWin;
  private multiplierPanel!: MultiplierPanel;
  private retriggerPopup!: RetriggerPopup;
  private bonusCounter!: BonusCounter;
  private logo: PIXI.Sprite;

  private _winLoopTimeout: any;
  private _isLooping: boolean = false;

  private reelSpinLoop: SoundInstance | undefined = new SoundInstance();
  private anticipationSoundInstance: SoundInstance | undefined = new SoundInstance();

  constructor() {
    super();

    this.bottomContainer = new PIXI.Container();
    this.topContainer = new PIXI.Container();
    this.paylineContainer = new PIXI.Container();
    this.content = new PIXI.Container();
    this.addChild(this.content);

    this.panelBack = new PIXI.Sprite(
      GameAssets.textures.gameAtlas.textures["panelBack.png"],
    );
    this.panelBack.anchor.set(0.5);
    this.panelBack.position.set(0, 10);

    this.panelFront = new PIXI.Sprite(
      GameAssets.textures.gameAtlas.textures["panelMain.png"],
    );
    this.panelFront.anchor.set(0.5);
    this.panelFront.position.set(0, 10);

    this.logo = new PIXI.Sprite(
      GameAssets.textures.gameAtlas.textures["logoPortrait.png"],
    );
    this.logo.anchor.set(0.5);

    this.curtains = Spine.from({
      skeleton: "panelCurtainsJson",
      atlas: "panelCurtainsAtlas",
    });
    this.curtains.state.setAnimation(0, "idle", true);
    this.curtains.position.set(0, -190);
    this.curtains.scale.set(0.2);

    const width: number = 1100;

    this.panelMask = new PIXI.Graphics()
      .rect(
        -width / 2,
        0,
        width,
        Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows,
      )
      .fill(0xffffff);
    this.bottomContainer.mask = this.panelMask;
    this.bottomContainer.addChild(this.panelMask);

    this.darkLayer = new PIXI.Graphics()
      .rect(
        -width / 2,
        0,
        width,
        Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows,
      )
      .fill(0x000000);
    this.darkLayer.alpha = 0.5;

    this.winLayer = new PIXI.Graphics()
      .rect(
        -width / 2,
        0,
        width,
        Symbol.SYMBOL_HEIGHT * GameEngine.getInstance().config.rows,
      )
      .fill(0x000000);
    this.winLayer.alpha = 0.7;

    this.winLayer.scale.set(0.6);
    this.winLayer.visible = false;
    this.bottomContainer.scale.set(0.6);
    this.bottomContainer.position.set(0, -143);
    this.paylineContainer.scale.set(1.1);
    this.paylineContainer.position.set(10, 10);
    this.darkLayer.scale.set(0.6);
    this.darkLayer.position.set(
      this.bottomContainer.position.x,
      this.bottomContainer.position.y,
    );
    this.winLayer.position.set(
      this.bottomContainer.position.x,
      this.bottomContainer.position.y,
    );
    this.topContainer.position.set(
      this.bottomContainer.position.x,
      this.bottomContainer.position.y,
    );
    this.topContainer.scale.set(0.6);

    this.winCounter = new WinCounter();
    this.winCounter.zIndex = 10;

    this._changeState(PanelState.IDLE);

    this.content.addChild(this.panelBack);
    this.content.addChild(this.darkLayer);
    this.content.addChild(this.bottomContainer);
    this.content.addChild(this.winLayer);
    this._createBonusPopup();
    this._createBonusCounter();
    this.content.addChild(this.panelFront);
    this.content.addChild(this.logo);
    this.content.addChild(this.curtains);
    this.content.addChild(this.topContainer);
    this.content.addChild(this.paylineContainer);
    this._createTorches();
    this._createMultipliers();
    this._createRetriggerPopup();

    this.content.addChild(this.winCounter);
  }

  private _createScatterParticle(
    globalPosition: PIXI.Point,
    globalTargetPosition: PIXI.Point,
  ) {
    const scatterParticle: ScatterParticle = ScatterParticle.pool.acquire();
    const position: PIXI.Point = this.content.toLocal(globalPosition);
    const targetPosition: PIXI.Point =
      this.content.toLocal(globalTargetPosition);
    scatterParticle.position.set(position.x, position.y);
    this.content.addChild(scatterParticle);
    return scatterParticle.collect(targetPosition.x, targetPosition.y);
  }

  private _createMultipliers() {
    this.multiplierPanel = new MultiplierPanel();
    this.content.addChild(this.multiplierPanel);
    this.multiplierPanel.hideCollector();
  }

  private _createBonusPopup() {
    this.bonusIntro = new BonusIntro();
    this.bonusIntro.y = 70;
    this.content.addChild(this.bonusIntro);

    this.bonusWin = new BonusWin();
    this.bonusWin.y = 70;
    this.content.addChild(this.bonusWin);
  }

  private _createRetriggerPopup() {
    this.retriggerPopup = new RetriggerPopup();
    this.retriggerPopup.y = 70;
    this.content.addChild(this.retriggerPopup);
    this.retriggerPopup.visible = false;
  }

  private _createTorches() {
    const torch1: Torch = new Torch();
    torch1.position.set(-420, 60);
    this.content.addChild(torch1);

    const torch2: Torch = new Torch();
    torch2.position.set(420, 60);
    torch2.scale.x = -1;
    this.content.addChild(torch2);

    this.torches.push(torch1);
    this.torches.push(torch2);
  }

  private _createBonusCounter() {
    this.bonusCounter = new BonusCounter();
    this.content.addChild(this.bonusCounter);
    this.bonusCounter.visible = false;
  }

  public init(data: InitialResponse) {
    this.panelData = data.panel;
    this._createReels();
  }

  public async showBonusIntro() {
    this.panelFront.texture =
      GameAssets.textures.gameAtlas.textures["panelFs.png"];
    console.log("won 5 free spins");
    await soundManager.playSound("freeSpinPopup");
    await this.bonusIntro.show();
  }

  public async hideBonusIntro() {
    this.bonusCounter.setCounter(this.data.freespins || 0);
    this.showCounter();
    this.multiplierPanel.showCollector();
    await this.bonusIntro.hide();
    await this._collectScatters();
  }

  public async showBonusWin() {
    this.reels.forEach((reel: IReel, index: number) => {
      gsap.to(reel, {
        y: 1000,
        ease: "back.in(1)",
        duration: 1,
        delay: index * 0.1,
      });
    });
    gsap.to(this.darkLayer, { alpha: 0 });
    this._hideRandomWilds();
    this.hideCounter();
    await this.bonusWin.show(this.data.totalWin || 0);
  }

  public async hideBonusWin() {
    return new Promise<void>(async (resolve) => {
      this.multiplierPanel.turnOff();
      await this.bonusWin.hide();
      this.multiplierPanel.hideCollector();
      this.panelFront.texture =
        GameAssets.textures.gameAtlas.textures["panelMain.png"];
      gsap.to(this.darkLayer, {
        alpha: 0.5,
        duration: 1,
      });
      this.reels.forEach((reel: IReel, index: number) => {
        reel.y = -1000;
        (reel as ReelSpin).resetHiddenSymbols();
        gsap.to(reel, {
          y: 0,
          ease: "back.out(1)",
          duration: 1,
          delay: index * 0.1,
          onComplete: resolve,
        });
      });
    });
  }

  public onLandscape(data?: any) {
    this.scale.set(1.3);
    this.y = -150;

    const k = Math.max(1.7 / data.ratio, 1) * 1.1;

    this.logo.texture =
      GameAssets.loading.loadingAtlas.textures["gameLogo.png"];
    this.logo.position.set(-580 / k, -170);
    this.logo.scale.set(0.4);
    this.bonusCounter.zIndex = -1;
    this.winCounter.onLandscape();
    this.bonusCounter.onLandscape();
  }

  public onPortrait(data?: any) {
    console.log("panel onPortrait ratio: " + data.ratio);
    this.y = -270;
    const maxRatio = 0.65;
    const maxScale = 1.7;
    const ratio = data.ratio;

    let scale: number;
    if (ratio >= maxRatio) {
      scale = maxScale;
    } else {
      // Linear interpolation between minScale and maxScale
      scale = ratio / maxRatio * maxScale;
      this.y = -270 + ((1 - (ratio / maxRatio)) * 100);
    }
    this.scale.set(scale);

    this.logo.texture =
      GameAssets.textures.gameAtlas.textures["logoPortrait.png"];
    this.logo.position.set(0, -300);
    this.logo.scale.set(1);

    this.bonusCounter.zIndex = 2;
    this.winCounter.onPortrait();
    this.bonusCounter.onPortrait();
  }

  public getState(): PanelState {
    return this.currentState;
  }

  public async setData(data: SpinResponse) {
    this.roundWin = 0;
    this.data = data;
    this.roundWin = this.data.roundWin;

    this.panelData = JSON.parse(JSON.stringify(data.panel));
    this.reels.forEach((reel, index) => {
      const reelData = this.panelData[index];
      reel.setData(reelData);
    });

    this._stopReels();
  }

  private _getWildByCoordinate(reel: number, row: number): Symbol | null {
    return (
      (this.bottomContainer.children.find(
        (child) =>
          child instanceof Symbol &&
          child.position.x ===
          reel * Symbol.SYMBOL_WIDTH - 2 * Symbol.SYMBOL_WIDTH &&
          child.position.y === (row + 0.5) * Symbol.SYMBOL_HEIGHT,
      ) as Symbol) || null
    );
  }

  private async _hideRandomWilds(duration: number = 1) {
    if (this.randomWilds.length) {
      this.randomWilds.forEach((randomWild: Symbol) => {
        gsap.to(randomWild, {
          ease: "back.in(1)", // Animation easing function
          duration,
          y: randomWild.y + 3 * Symbol.SYMBOL_HEIGHT,
          onComplete: () => {
            Symbol.pool.release(randomWild);
          },
        });
      });
      this.randomWilds.length = 0;
      this.multiplierPanel.turnOff();
    }
  }

  private async _showRandomWilds() {
    if (this.data.randomWilds && this.data.randomWilds.length) {
      if (
        GlobalState.getInstance().state === GameState.BONUS ||
        GlobalState.getInstance().state === GameState.BONUS_TO_MAIN
      ) {
        await this._showRandomWildsBonus();
      } else {
        await this._showRandomWildsMain();
      }

      await delay(1000);
    }

    this._changeState(PanelState.STOPPING);
  }

  private prevWildPositions: Map<number, { reel: number, row: number }> = new Map();

  private async _showRandomWildsMain() {
    // 1. SAFETY LOCK: Wait until server data is actually present
    // We check this.data (the SpinResponse) and the specific array
    while (!this.data || !this.data.randomWilds) {
      await delay(100); // Check every 100ms
    }

    // 2. If data exists but is empty, just exit
    if (this.data.randomWilds.length === 0) {
      return;
    }

    this.torches.forEach(async (torch: Torch) => {
      torch.startTorch();
    });
    const multiplier: number = parseInt(
      this.data.randomWilds[0].symbol.split("_")[1],
    );
    await this.multiplierPanel.turnOnMain(multiplier);

    await delay(500);
    this.data.randomWilds.forEach((data: RandomWild, index: number) => {
      (this.reels[data.coordinates.reel] as ReelSpin).addRowToHide(
        data.coordinates.row,
      );
      const randomWild = Symbol.pool.acquire();
      randomWild.originalParent = this.bottomContainer;
      randomWild.setType("10");
      randomWild.setMultiplier(multiplier);
      randomWild.position.set(
        data.coordinates.reel * Symbol.SYMBOL_WIDTH - 2 * Symbol.SYMBOL_WIDTH,
        (data.coordinates.row + 0.5) * Symbol.SYMBOL_HEIGHT,
      );
      this.bottomContainer.addChild(randomWild);
      const difY: number = -1000;
      randomWild.y += difY;
      gsap.to(randomWild, {
        delay: this.isQuickStop ? 0 : 0.5 * index,
        duration: 0.5,
        y: randomWild.y - difY,
        onStart: () => {
          const prev = this.prevWildPositions.get(index) || { reel: -1, row: -1 };

          if (prev.reel !== data.coordinates.reel || prev.row !== data.coordinates.row) {
            soundManager.playSound("randomWildMovement");
          }

          this.prevWildPositions.set(index, {
            reel: data.coordinates.reel,
            row: data.coordinates.row
          });
        }

      });
      this.randomWilds.push(randomWild);
    });
    this.emit("showRandomWilds");
  }

  private async _showRandomWildsBonus() {
    if (!this.data.randomWilds || !this.data.randomWilds.length) {
      return;
    }

    if (!this.randomWilds.length) {
      const multiplier: number = parseInt(
        this.data.randomWilds[0].symbol.split("_")[1],
      );
      this.data.randomWilds.forEach((data: RandomWild, index: number) => {
        (this.reels[data.coordinates.reel] as ReelSpin).addRowToHide(
          data.coordinates.row,
        );
        const randomWild = Symbol.pool.acquire();
        randomWild.originalParent = this.bottomContainer;
        randomWild.setType("10");
        randomWild.setMultiplier(multiplier);
        randomWild.position.set(
          data.coordinates.reel * Symbol.SYMBOL_WIDTH - 2 * Symbol.SYMBOL_WIDTH,
          (data.coordinates.row + 0.5) * Symbol.SYMBOL_HEIGHT,
        );
        this.bottomContainer.addChild(randomWild);
        const difY: number = -1000;
        randomWild.y += difY;
        gsap.to(randomWild, {
          delay: this.isQuickStop ? 0 : 0.5 * index,
          duration: 0.5,
          y: randomWild.y - difY,
        });
        this.randomWilds.push(randomWild);
      });
    }
    const multiplier: number = parseInt(
      this.data.randomWilds[0].symbol.split("_")[1],
    );
    console.log("turned on multiplier animation");
    this.multiplierPanel.turnOn(multiplier);

    await delay(500);
    this.data.randomWilds.forEach((data: RandomWild, index: number) => {
      (this.reels[data.coordinates.reel] as ReelSpin).addRowToHide(
        data.coordinates.row,
      );
      const randomWild = this.randomWilds[index];
      if (!randomWild) {
        return;
      }
      randomWild.setMultiplier(multiplier);
      gsap.to(randomWild, {
        delay: this.isQuickStop ? 0 : 0.5 * index,
        duration: 0.5,
        x:
          data.coordinates.reel * Symbol.SYMBOL_WIDTH - 2 * Symbol.SYMBOL_WIDTH,
        y: (data.coordinates.row + 0.5) * Symbol.SYMBOL_HEIGHT,
        onStart: () => {
          const prev = this.prevWildPositions.get(index) || { reel: -1, row: -1 };

          if (prev.reel !== data.coordinates.reel || prev.row !== data.coordinates.row) {
            soundManager.playSound("randomWildMovement");
          }

          this.prevWildPositions.set(index, {
            reel: data.coordinates.reel,
            row: data.coordinates.row
          });
        }
      });
    });
  }

  private _stopReels() {
    if (this.panelData.length && this.currentState === PanelState.SPIN_END) {
      this._changeState(PanelState.RANDOM_WILD);
    }
  }

  private _createReels() {
    for (let i = 0; i < GameEngine.getInstance().config.reels; i++) {
      const reel = new ReelSpin(this.panelData[i], i);
      reel.x =
        i * Symbol.SYMBOL_WIDTH -
        (GameEngine.getInstance().config.reels * Symbol.SYMBOL_WIDTH) / 2 +
        Symbol.SYMBOL_WIDTH / 2;
      this.bottomContainer.addChild(reel);
      this.reels.push(reel);
      reel.on(ReelState.IDLE, this._onReelStop, this);
      reel.on(ReelState.SPIN_END, this._onReelSpinEnd, this);
    }
  }

  private _onReelStop() {
    let allStopped: boolean = true;
    this.reels.forEach((reel: IReel) => {
      if (reel.getState() !== ReelState.IDLE) {
        allStopped = false;
      }
    });
    if (allStopped) {
      if (this.reelSpinLoop) {
        soundManager.stopSoundInstance(this.reelSpinLoop!);
        this.reelSpinLoop = undefined;
      }

      this.curtains.state.setAnimation(0, "shake_end", false);
      this.curtains.state.addAnimation(0, "idle", true);
      this._changeState(PanelState.STOP);
    }
  }

  private _onReelSpinEnd(reel: Reel) {
    let allSpin: boolean = true;
    this.reels.forEach((reel: IReel) => {
      if (reel.getState() !== ReelState.SPIN_END) {
        allSpin = false;
      }
    });
    if (allSpin) {
      this._changeState(PanelState.SPIN_END);
      this._stopReels();
    }
  }

  // Method to change the state and execute the corresponding actions
  private _changeState(newState: PanelState) {
    if (this.currentState === newState) {
      return; // No change if the same state
    }

    // Enter logic for the new state
    this.currentState = newState;
    switch (this.currentState) {
      case PanelState.IDLE:
        this._onEnterIdle();
        break;
      case PanelState.SPIN:
        this._onEnterSpin();
        break;
      case PanelState.WIN:
        this._onRoundWin();
        break;
      case PanelState.MATCH:
        this._onMatch();
        break;
      case PanelState.SCATTER_MATCH:
        (async () => await this._onScatterMatch())();
        soundManager.playSound("wildBonus");
        break;
      case PanelState.EXPANDED_WILD:
        this._onExpandedWilds();
        break;
      case PanelState.STOPPING:
        this._onStopping();
        break;
      case PanelState.RANDOM_WILD:
        this._showRandomWilds();
        break;
      case PanelState.STOP:
        if (this.data.wins.length) {
          this._changeState(PanelState.MATCH);
        } else if (
          this.data.scatters &&
          this.data.scatters.coordinates.length
        ) {
          this._changeState(PanelState.SCATTER_MATCH);
        } else {
          this._changeState(PanelState.IDLE);
        }
        break;
    }

    this.emit(this.currentState);
  }

  // Define custom logic for entering and exiting each state
  private _onEnterIdle() {
    if (
      GlobalState.getInstance().state === GameState.BONUS ||
      GlobalState.getInstance().state === GameState.MAIN_TO_BONUS
    ) {
    } else {
      this.torches.forEach((torch: Torch) => {
        torch.endTorch();
      });
      this.multipliers.forEach((multiplier: Multiplier) => {
        multiplier.turnOff();
      });
    }
  }

  public showCounter() {
    this.bonusCounter.visible = true;
  }

  public hideCounter() {
    this.bonusCounter.visible = false;
  }

  private async _onEnterSpin() {
    this._isLooping = false;
    if (this._winLoopTimeout) {
      clearTimeout(this._winLoopTimeout);
      this._winLoopTimeout = null;
    }

    // 1. Reset Overlays
    this.winLayer.visible = false;
    this.paylineContainer.removeChildren();

    // 2. The "Old Method" Cleanup
    // We iterate through topContainer directly to catch EVERYTHING 
    // that might be stuck in the front layer.
    while (this.topContainer.children.length > 0) {
      const child = this.topContainer.children[0] as any;
      if (child && child.originalParent) {
        gsap.killTweensOf(child);
        // This is the exact method the old code used
        moveObjectToNewParent(child, child.originalParent);
      } else {
        // Backup for things without an originalParent
        this.topContainer.removeChild(child);
      }
    }

    // 3. Clear our tracking arrays
    this.winSymbols.length = 0;
    this.randomWilds.forEach(w => {
      gsap.killTweensOf(w);
      Symbol.pool.release(w);
    });
    this.randomWilds.length = 0;

    // 4. Standard Reel Reset
    this.reels.forEach(reel => {
      reel.resetData();
      if (reel.parent !== this.bottomContainer) {
        moveObjectToNewParent(reel, this.bottomContainer);
      }
    });

    // ... standard spin logic below
    this._resetQuickStop();
    this.currentReelIndex = 0;

    // ... (rest of your spin logic)
    if (GlobalState.getInstance().state === GameState.BONUS) {
      this.bonusCounter.decrementCounter();
    } else {
      this._hideRandomWilds(0.5);
    }

    this.curtains.state.setAnimation(0, "shake_start", false);
    this.curtains.state.addAnimation(0, "shake_loop", true);
    this.reels[this.currentReelIndex].triggerSpin();
    this.currentReelIndex++;
    this._startNextSpin();
    this.reelSpinLoop = await soundManager.playSound("reelSpinLoop");
  }

  private _startNextSpin() {
    this.stopNextId = setTimeout(() => {
      if (this.reels[this.currentReelIndex]) {
        this.reels[this.currentReelIndex].triggerSpin();
        this.currentReelIndex++;
        this._startNextSpin();
      }
    }, SPIN_REEL_INTERVAL);
  }

  private async _onRoundWin() {
    this._resetQuickStop();

    // 1. Show the win counter/popup as usual
    await this.winCounter.showPrize(this.roundWin, this.data.odd);

    // 2. ONLY start the loop if we are in MAIN game mode
    const isMainGame = GlobalState.getInstance().state === GameState.MAIN;

    if (isMainGame && this.data.wins && this.data.wins.length > 0) {
      this._isLooping = true;
      this._runPaylineLoop(0);
    }

    // 3. Continue with game flow (Scatters, Free Spins, etc.)
    if (this.data.scatters && this.data.scatters.coordinates.length) {
      this._changeState(PanelState.SCATTER_MATCH);
    } else {
      this._changeState(PanelState.IDLE);
    }
  }

  private async _runPaylineLoop(index: number) {
    // Check if we are still in Main Game. If not, kill the loop and clean up.
    const isMainGame = GlobalState.getInstance().state === GameState.MAIN;

    if (!this._isLooping || this.currentState === PanelState.SPIN || !isMainGame) {
      // Final cleanup
      this.winSymbols.forEach(s => moveObjectToNewParent(s, s.originalParent));
      this.winSymbols.length = 0;
      this.paylineContainer.removeChildren();
      this.winLayer.visible = false;
      this._isLooping = false;
      return;
    }

    const currentWin = this.data.wins[index];
    await this._showSingleWinInLoop(currentWin);

    this._winLoopTimeout = setTimeout(() => {
      const nextIndex = (index + 1) % this.data.wins.length;
      this._runPaylineLoop(nextIndex);
    }, 1000);
  }

  private async _showSingleWinInLoop(winData: Win) {
    // 1. Clear previous visuals (if any)
    this.paylineContainer.removeChildren();
    this.winSymbols.forEach((symbol: Symbol) => {
      moveObjectToNewParent(symbol, symbol.originalParent);
    });
    this.winSymbols.length = 0;

    // 2. Start showing the new win
    this.winLayer.visible = true;
    const currentPaylines: Payline[] = [];

    const winPromises = winData.coordinates.map(async (coordinates) => {
      const symbol: Symbol =
        this._getWildByCoordinate(coordinates.reel, coordinates.row) ||
        this.reels[coordinates.reel].getSymbol(coordinates.row);

      this.winSymbols.push(symbol);
      moveObjectToNewParent(symbol, this.topContainer);

      if (typeof winData.payline !== "undefined") {
        const payline: Payline = Payline.pool.acquire(winData.payline + 1);
        payline.zIndex = 20;
        this.paylineContainer.addChild(payline);
        currentPaylines.push(payline);
      }
      return symbol.win(this.isQuickStop);
    });

    // We wait for the animations to play
    await Promise.all(winPromises);
  }

  private async _onMatch(): Promise<void> {
    await soundManager.playSound("paylineShow");
    await Promise.all(this.data.wins.map((winData) => this._showWin(winData)));
    this._changeState(PanelState.WIN);
  }

  private async _collectScatters() {
    if (!this.data.scatters) {
      return;
    }
    for (const coordinates of this.data.scatters.coordinates) {
      const symbol = this.reels[coordinates.reel].getSymbol(coordinates.row);
      symbol.win();
      await delay(500);
      moveObjectToNewParent(symbol, this.topContainer);
      await this._createScatterParticle(
        symbol.getGlobalPosition(),
        this.multiplierPanel.getFireGlobalPosition(),
      );
      moveObjectToNewParent(symbol, symbol.originalParent);
      this.multiplierPanel.collectOne();
    }
  }

  private async _onScatterMatch(): Promise<void> {
    if (this.data.scatters && this.data.scatters.coordinates.length) {
      if (GlobalState.getInstance().state === GameState.BONUS) {
        if (!this.multiplierPanel.isFull()) {
          await this._collectScatters();
          await delay(500);

          if (
            this.data.freespins &&
            this.data.freespins > this.bonusCounter.getFreespins()
          ) {
            await this.retriggerPopup.show(
              this.data.freespins - this.bonusCounter.getFreespins(),
            );
            this.bonusCounter.setCounter(this.data.freespins);
            await delay(500);
          }
        }
      } else {
        await this._showWin(this.data.scatters);
      }
    }

    this._changeState(PanelState.IDLE);
  }

  private async _onExpandedWilds(): Promise<void> {
    this._changeState(PanelState.MATCH);
  }

  private _resetQuickStop() {
    this.isQuickStop = false;
    this.emit("resetQuickStop");
  }

  private async _showWin(winData: Win) {
    this.winLayer.visible = true;

    const paylines: Payline[] = [];
    const winPromises = winData.coordinates.map(async (coordinates) => {
      const symbol: Symbol =
        this._getWildByCoordinate(coordinates.reel, coordinates.row) ||
        this.reels[coordinates.reel].getSymbol(coordinates.row);
      this.winSymbols.push(symbol);
      moveObjectToNewParent(symbol, this.topContainer);
      if (typeof winData.payline !== "undefined") {
        const payline: Payline = Payline.pool.acquire(winData.payline + 1);
        payline.zIndex = 20;
        this.paylineContainer.addChild(payline);
        paylines.push(payline);

      }
      return symbol.win(this.isQuickStop);
    });

    await Promise.all(winPromises); // Wait for all win() calls to complete
    this.winSymbols.forEach((symbol: Symbol) => {
      moveObjectToNewParent(symbol, symbol.originalParent);
    });

    paylines.forEach((payline) => {
      Payline.pool.release(payline);
    });

    this.winSymbols.length = 0;
    this.winLayer.visible = false;
  }

  private async _onStopping() {
    this.currentReelIndex = 0;
    this.currentSccatersAmount = 0;
    if (this.stopNextId) {
      clearTimeout(this.stopNextId);
    }
    if (this.isQuickStop) {
      if (this.reelSpinLoop) {
        soundManager.stopSoundInstance(this.reelSpinLoop!);
        this.reelSpinLoop = undefined;
      }

      for (let i = this.currentReelIndex; i < this.reels.length; i++) {
        this.reels[i].triggerStop(this.isQuickStop);
      }
    } else {
      this._stopNext();
    }
  }

  private _triggerStopReel(delay: number = STOP_REEL_INTERVAL) {
    this.reels[this.currentReelIndex].triggerStop(this.isQuickStop);
    this.currentSccatersAmount +=
      this.reels[this.currentReelIndex].getScattersAmount();
    this.currentReelIndex++;
    this.stopNextId = setTimeout(async () => {
      const justStoppedReel = this.reels[this.currentReelIndex - 1];
      const hasScattersOnThisReel = justStoppedReel.getScattersAmount() > 0;

      // If we're in anticipation mode (already have scatters), use different audio
      if (this.currentSccatersAmount > 2) {
        if (hasScattersOnThisReel) {
          // await soundManager.playSound("scatterLand");
        } else {
          // Still in anticipation mode, but this reel didn't help
          // await soundManager.playSound("anticipationStop"); // or just silence
        }
      } else if (this.currentSccatersAmount < 3
        && GlobalState.getInstance().state !== GameState.BONUS) {
        // Normal mode - no scatters yet
        await soundManager.playSound("reelStop");
      } else if (GlobalState.getInstance().state === GameState.BONUS) {
        await soundManager.playSound("reelStop");
      }
      this._stopNext();
    }, delay);
  }

  private _stopNext() {
    if (this.isQuickStop) {
      if (this.stopNextId) {
        clearTimeout(this.stopNextId);
        this.stopNextId = null;
        for (let i = this.currentReelIndex; i < this.reels.length; i++) {
          this.reels[i].triggerStop(this.isQuickStop);
        }
      }
    } else if (this.reels[this.currentReelIndex]) {
      this.stopNextId = null;
      if (
        this.currentSccatersAmount >= 2 &&
        GlobalState.getInstance().state !== GameState.BONUS
      ) {
        this.reels[this.currentReelIndex].startAnticipation();

        console.log("started anticipation");
        (async () => {
          this.anticipationSoundInstance = await soundManager.playSound("anticipation")
        })();

        if (this.reelSpinLoop) {
          soundManager.stopSoundInstance(this.reelSpinLoop!);
          this.reelSpinLoop = undefined;
        }

        // console.log("stopped anticipation");
        // soundManager.stopSoundInstance(this.anticipationSoundInstance!);
        this.stopNextId = setTimeout(() => {
          this._triggerStopReel(5 * STOP_REEL_INTERVAL);
        }, STOP_REEL_ANTICIPATION_INTERVAL);
      } else {
        this._triggerStopReel();
      }
    }
  }

  public spin() {
    this._changeState(PanelState.SPIN);
  }

  public async quickStop() {
    this.isQuickStop = true;
    console.log("QUICK STOP", this.currentState);
    switch (this.currentState) {
      case PanelState.SPIN:
      case PanelState.SPIN_END:
        break;
      case PanelState.STOPPING:
        this._onStopping();
        if (this.anticipationSoundInstance) {
          soundManager.stopSoundInstance(this.anticipationSoundInstance!);
          this.anticipationSoundInstance = undefined;
        }
        break;
      case PanelState.MATCH:
        break;
      case PanelState.WIN:
        this.winCounter.quickStop();
        break;
    }
  }
}
